import React, { useState, useEffect } from 'react';
import { useSession, signIn } from 'next-auth/react';
import { loadSettings } from 'src/api/payerApi';
import {
  getDigitalCollectionTerms,
  removeDigitalCollectionTerms,
  getDigitalCollectionUserPreference,
  getTermsAndAgreement,
  setDigitalCollectionTerms,
  setTermsAndAgreement,
  setTemporaryPass,
  removeTerms,
  getTemporaryPass,
} from 'src/helpers/localStorageHelper';

import { setAuthToken } from 'src/api/api';
import { getToday, addDays, convertStrToUtcDate } from 'src/helpers/dateHelper';
import { datadogRum } from '@datadog/browser-rum';

export const AuthContext = React.createContext();

export function AuthProvider({ children }) {
  const { data: session, status: status } = useSession();
  const [settings, setSettings] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasTermsAndAgreement, setHasTermsAndAgreement] = useState(false);
  const [hasDigitalCollectionTerms, setHasDigitalCollectionTerms] = useState(false);
  const [isTemporaryPassValid, setIsTemporaryPassValid] = useState(false);

  function updateTermsAndAgreement() {
    setTermsAndAgreement(session.user.id, getToday());
    setHasTermsAndAgreement(getTermsAndAgreement(session.user.id) !== null);
  }
  function updateDigitalCollectionTerms() {
    setDigitalCollectionTerms(session.user.id, getToday());
    setHasDigitalCollectionTerms(getDigitalCollectionTerms(session.user.id) !== null);
  }
  function revokeTerms() {
    removeTerms(session.user.id);
    updateTemporaryPass();
  }

  function updateTemporaryPass() {
    var expiryDate = addDays(getToday(), 1);
    setTemporaryPass(session.user.id, expiryDate);
  }

  function checkTemporaryPass() {
    const temporaryPass = convertStrToUtcDate(getTemporaryPass(session.user.id));
    setIsTemporaryPassValid(temporaryPass !== null && getToday() < temporaryPass);
  }

  function onLoadSettingsSuccess(data) {
    setSettings(data);
  }

  function refreshSettings() {
    loadSettings(onLoadSettingsSuccess, () => {});
  }

  const logout = async () => {
    const idToken = session?.idToken;
    if (session?.user?.id && getDigitalCollectionUserPreference(session.user.id) === 'no') {
      removeDigitalCollectionTerms(session.user.id);
    }
    await fetch('/api/auth/clear-session').catch(() => {});

    // Clear NextAuth cookies client-side
    const cookiesToClear = [
      '__Secure-next-auth.session-token',
      'next-auth.session-token',
      'next-auth.callback-url',
      '__Secure-next-auth.callback-url',
      '__Host-next-auth.csrf-token',
      'next-auth.csrf-token',
      '__Secure-next-auth.state',
      '__Secure-next-auth.pkce.code_verifier',
    ];

    cookiesToClear.forEach(name => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; secure`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; secure; samesite=lax`;
    });
    
    // Build IdentityServer endsession URL
    const params = new URLSearchParams({
      post_logout_redirect_uri: 'https://www.motus.is/greidendur/greidendavefur',
    });
    
    if (idToken) {
      params.append('id_token_hint', idToken);
    }
    
    // Redirect directly to IdentityServer
    window.location.href = `${process.env.NEXT_PUBLIC_STS_URL}/connect/endsession?${params.toString()}`;
  };

  useEffect(() => {
    if (status === 'unauthenticated') {
      setIsInitialized(true);
    }
  }, [status]);

  useEffect(() => {
    if (session?.error === 'RefreshAccessTokenError') {
      signIn('motus-identity-server', { redirect: false });
    }
  }, [session?.error]);

  useEffect(() => {
    if (session?.accessToken) {
      datadogRum.setUser({ id: session.user.id });
      setAuthToken(session.accessToken);
      setIsInitialized(true);
      refreshSettings();
      setHasTermsAndAgreement(getTermsAndAgreement(session.user.id) !== null);
      setHasDigitalCollectionTerms(getDigitalCollectionTerms(session.user.id) !== null);
      checkTemporaryPass();
    }
  }, [session?.accessToken]);
  /** user.name er (yfirleitt?) array sem inniheldur nafn greiðandans tvisvar
   * auk notandanafnsins hans (sem er GUID). Reynum að velja rétt nafn til að birta á vefnum. */
  const tryGetName = (user) => {
    if (!user || !user.name) return undefined;
    if (typeof user.name === 'string') return user.name;
    if (!Array.isArray(user.name)) return undefined;

    const names = user.name.filter((x) => x !== user.username);
    return names.length > 0 ? names[0] : user.name[0];
  };
  return (
    <AuthContext.Provider
      value={{
        isLoading: status === 'loading' || !isInitialized,
        isLoggedIn: status === 'authenticated',
        name: tryGetName(session?.user),
        ssn: session?.user?.ssn,
        needsToAcceptTerms: !hasTermsAndAgreement && !isTemporaryPassValid,
        hasDigitalCollectionTerms: hasDigitalCollectionTerms,
        settings,
        setSettings,
        refreshSettings,
        updateTermsAndAgreement,
        updateDigitalCollectionTerms,
        revokeTerms,
        updateTemporaryPass,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
